import {
  BzAddress,
  BzDateFns,
  formatJobClass,
  formatPercentage,
  IsoDateString,
  MaintenancePlanConfirmationData,
  MaintenancePlanConfirmationPortalData,
  MaintenancePlanStatus,
  maintenancePlanStatusDisplayNames,
  parsePdfLayoutConfig,
  phoneUtils,
  shouldRenderMaintenancePlanConfirmationAccountDisplayName,
  TimeZoneId,
  unauthMaintenancePlanConfirmationDataProcedurePath,
} from '@breezy/shared'
import { faCalendarCheck, faDownload } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'antd'
import React from 'react'
import { useParams } from 'react-router-dom'
import { z } from 'zod'
import { FullScreenLoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner'
import { trpc } from '../../hooks/trpc'
import { useDownloadMaintenancePlanConfirmationUnauth } from './use-download-maintenance-plan-confirmation-unauth'

const ConfirmationNotFound = React.memo(() => (
  <div className="flex h-screen w-screen items-center justify-center p-6">
    <div className="max-w-md text-center">
      <h2 className="text-xl font-semibold">We couldn't find that plan.</h2>
      <p className="mt-2 text-sm text-gray-600">Please check the link you used.</p>
    </div>
  </div>
))

const StatusBadge = React.memo(({ label, tone }: { label: string; tone: 'green' | 'gray' | 'red' | 'amber' }) => {
  const toneClasses = {
    green: 'bg-green-100 text-green-800',
    gray: 'bg-gray-100 text-gray-800',
    red: 'bg-red-100 text-red-800',
    amber: 'bg-amber-100 text-amber-800',
  }[tone]
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${toneClasses}`}>
      {label}
    </span>
  )
})

const formatDate = (iso: string | undefined, tz: string): string | undefined => {
  if (!iso) return undefined
  try {
    return BzDateFns.formatFromISO(iso as unknown as IsoDateString, 'MMMM d, yyyy', tz as unknown as TimeZoneId)
  } catch {
    return iso
  }
}

const normalizeWebsiteHref = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined

  if (/^https?:\/\//i.test(trimmed)) return trimmed

  // Any non-http(s) scheme is treated as unsafe for direct linking.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return undefined

  return `https://${trimmed}`
}

const normalizeImageSrc = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined

  try {
    const parsed = new URL(trimmed)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? trimmed : undefined
  } catch {
    return undefined
  }
}

const ConfirmationPageShell = React.memo(({ children }: { children: React.ReactNode }) => (
  <main
    data-testid="maintenance-plan-confirmation-page"
    className="min-h-screen bg-gray-100 px-4 py-6 text-gray-900 sm:px-6 sm:py-10 lg:px-8"
  >
    <article
      data-testid="maintenance-plan-confirmation-container"
      className="mx-auto w-full max-w-3xl rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-8"
    >
      {children}
    </article>
  </main>
))

type CompanyBrandHeaderProps = {
  billingProfile: MaintenancePlanConfirmationData['company']['billingProfile']
  businessLogoSrc: string | undefined
  logoPosition: 'left' | 'right'
  websiteHref: string | undefined
  websiteLabel: string | undefined
}

const CompanyBrandHeader = React.memo(
  ({ billingProfile, businessLogoSrc, logoPosition, websiteHref, websiteLabel }: CompanyBrandHeaderProps) => {
    const contactItems: React.ReactNode[] = [
      phoneUtils.tryFormat(billingProfile.businessBillingContactPhoneNumber),
      billingProfile.businessBillingEmailAddress,
    ]

    if (websiteLabel) {
      contactItems.push(
        websiteHref ? (
          <a href={websiteHref} target="_blank" rel="noreferrer" className="underline">
            {websiteLabel}
          </a>
        ) : (
          websiteLabel
        ),
      )
    }

    const headerDirectionClass = logoPosition === 'right' ? 'sm:flex-row-reverse' : 'sm:flex-row'
    const companyInfoAlignmentClass =
      logoPosition === 'right' || !businessLogoSrc ? 'text-left' : 'text-left sm:text-right'

    return (
      <div
        data-testid="maintenance-plan-confirmation-brand-header"
        data-logo-position={logoPosition}
        className={`mb-5 flex flex-col gap-4 border-b border-gray-200 pb-5 ${headerDirectionClass} sm:items-center`}
      >
        {businessLogoSrc ? (
          <img
            src={businessLogoSrc}
            alt={`${billingProfile.businessLegalName} logo`}
            className="max-h-20 w-full max-w-56 object-contain sm:max-w-64"
          />
        ) : null}
        <div className={`min-w-0 flex-1 ${companyInfoAlignmentClass}`}>
          <div className="text-xl font-semibold">
            {billingProfile.businessLegalName}
            {billingProfile.contractorLicenseNumber ? (
              <>
                {' '}
                <span className="whitespace-nowrap text-sm font-medium text-gray-500">
                  CL # {billingProfile.contractorLicenseNumber}
                </span>
              </>
            ) : null}
          </div>
          <div className="mt-1 text-sm text-gray-600">
            {BzAddress.formatAddressSingleLine(billingProfile.businessAddress)}
          </div>
          <div className="mt-1 text-sm text-gray-600">
            {contactItems.map((item, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 ? <span className="text-gray-400"> | </span> : null}
                {item}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    )
  },
)

const ConfirmationStatusOnly = React.memo(
  ({ data }: { data: Extract<MaintenancePlanConfirmationPortalData, { downloadable: false }> }) => {
    const statusLabel =
      data.status === MaintenancePlanStatus.PENDING
        ? 'Pending activation'
        : data.statusDetails.scheduledActivationAt
          ? `Scheduled for ${formatDate(data.statusDetails.scheduledActivationAt, data.companyTimezone) ?? ''}`
          : 'Scheduled — activation date pending'

    return (
      <ConfirmationPageShell>
        <div className="flex items-start gap-3 border-b border-gray-200 pb-5">
          <FontAwesomeIcon icon={faCalendarCheck} className="text-2xl text-gray-500" />
          <div>
            <h1 className="text-2xl font-semibold">{data.companyName} Maintenance Plan</h1>
            <div className="mt-3">
              <StatusBadge label={statusLabel} tone="amber" />
            </div>
          </div>
        </div>
        <p className="mt-6 text-sm text-gray-700">For questions about your plan, please contact {data.companyName}.</p>
      </ConfirmationPageShell>
    )
  },
)

const ConfirmationDownloadable = React.memo(
  ({
    confirmation,
    companyGuid,
    maintenancePlanGuid,
  }: {
    confirmation: MaintenancePlanConfirmationData
    companyGuid: string
    maintenancePlanGuid: string
  }) => {
    const { downloadConfirmation, isDownloading } = useDownloadMaintenancePlanConfirmationUnauth({
      companyGuid,
      maintenancePlanGuid,
      planName: confirmation.plan.name,
    })

    const tz = confirmation.company.timezone
    const billingProfile = confirmation.company.billingProfile
    const websiteLabel =
      billingProfile.businessWebsiteDisplayName?.trim() || billingProfile.businessWebsiteURL?.trim() || undefined
    const websiteHref = normalizeWebsiteHref(billingProfile.businessWebsiteURL)
    const businessLogoSrc = normalizeImageSrc(billingProfile.businessLogoURL)
    const pdfLayoutConfig = parsePdfLayoutConfig(confirmation.company.pdfLayoutConfig)
    const activatedAtDisplay = formatDate(confirmation.activatedAt, tz) ?? ''
    const statusTone = (() => {
      switch (confirmation.status) {
        case MaintenancePlanStatus.ACTIVE:
          return 'green' as const
        case MaintenancePlanStatus.LAPSED:
        case MaintenancePlanStatus.EXPIRED:
          return 'red' as const
        case MaintenancePlanStatus.CANCELED:
          return 'gray' as const
      }
    })()
    const shouldRenderStatus = confirmation.status !== MaintenancePlanStatus.ACTIVE
    const statusSubtext = (() => {
      switch (confirmation.status) {
        case MaintenancePlanStatus.CANCELED:
          return confirmation.statusDetails.canceledAt
            ? `on ${formatDate(confirmation.statusDetails.canceledAt, tz) ?? ''}`
            : ''
        case MaintenancePlanStatus.EXPIRED:
          return confirmation.statusDetails.expiredAt
            ? `on ${formatDate(confirmation.statusDetails.expiredAt, tz) ?? ''}`
            : ''
        case MaintenancePlanStatus.LAPSED:
          return confirmation.statusDetails.lapsedAt
            ? `on ${formatDate(confirmation.statusDetails.lapsedAt, tz) ?? ''}`
            : ''
      }
      return ''
    })()
    const accountDisplayName = shouldRenderMaintenancePlanConfirmationAccountDisplayName({
      primaryContactName: confirmation.customer.primaryContactName,
      accountDisplayName: confirmation.customer.accountDisplayName,
    })
      ? confirmation.customer.accountDisplayName
      : undefined

    return (
      <ConfirmationPageShell>
        <header>
          <CompanyBrandHeader
            billingProfile={billingProfile}
            businessLogoSrc={businessLogoSrc}
            logoPosition={pdfLayoutConfig.header.logoPosition}
            websiteHref={websiteHref}
            websiteLabel={websiteLabel}
          />
          <div
            data-testid="maintenance-plan-confirmation-title-block"
            className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold">Maintenance Plan Confirmation</h1>
              <div className="mt-2 text-sm text-gray-600">
                <strong>Activated:</strong> {activatedAtDisplay}
              </div>
              {shouldRenderStatus ? (
                <div className="mt-3 flex items-center gap-3">
                  <StatusBadge label={maintenancePlanStatusDisplayNames[confirmation.status]} tone={statusTone} />
                  {statusSubtext ? <span className="text-sm text-gray-500">{statusSubtext}</span> : null}
                </div>
              ) : null}
            </div>
            <div className="shrink-0">
              <Button
                type="primary"
                icon={<FontAwesomeIcon icon={faDownload} />}
                loading={isDownloading}
                data-dd-action-name="bz-maintenance-plan-download-confirmation-pdf"
                onClick={() => {
                  void downloadConfirmation()
                }}
              >
                Download PDF
              </Button>
            </div>
          </div>
        </header>

        <section className="mb-6 border-t border-gray-200 pt-5">
          <h2 className="mb-2 text-lg font-semibold">Customer</h2>
          <div>
            <strong>{confirmation.customer.primaryContactName}</strong>
          </div>
          {accountDisplayName ? <div>{accountDisplayName}</div> : null}
          <div className="mt-2 text-sm text-gray-700">
            {confirmation.customer.serviceAddress.line1}
            {confirmation.customer.serviceAddress.line2 ? `, ${confirmation.customer.serviceAddress.line2}` : ''},{' '}
            {confirmation.customer.serviceAddress.city}, {confirmation.customer.serviceAddress.stateAbbreviation}{' '}
            {confirmation.customer.serviceAddress.zipCode}
          </div>
        </section>

        <section className="mb-6 border-t border-gray-200 pt-5">
          <h2 className="mb-2 text-lg font-semibold">Plan Summary</h2>
          <div className="text-base font-semibold">{confirmation.plan.name}</div>
          <div className="text-sm text-gray-700">Yearly Visits: {confirmation.plan.yearlyVisits}</div>
          {confirmation.plan.coveredEquipment.length > 0 ? (
            <div className="mt-2">
              <div className="text-sm font-medium">Covered Equipment</div>
              <ul className="ml-4 list-disc text-sm text-gray-700">
                {confirmation.plan.coveredEquipment.map((e, idx) => (
                  <li key={`${e.type}-${idx}`}>
                    {e.type}
                    {e.nickname ? <span className="ml-2 text-gray-500">({e.nickname})</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {confirmation.plan.benefits.length > 0 ? (
            <div className="mt-2">
              <div className="text-sm font-medium">Benefits</div>
              <ul className="ml-4 list-disc text-sm text-gray-700">
                {confirmation.plan.benefits.map((b, idx) => (
                  <li key={`${b.name}-${idx}`}>
                    <strong>{b.name}</strong> — {b.description}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {confirmation.plan.discounts.length > 0 ? (
            <div className="mt-2">
              <div className="text-sm font-medium">Discounts</div>
              <ul className="ml-4 list-disc text-sm text-gray-700">
                {confirmation.plan.discounts.map((discount, idx) => (
                  <li key={`${discount.jobClass}-${idx}`}>
                    {formatPercentage(discount.discountRate, 0)} off {formatJobClass(discount.jobClass)} work
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        <section className="mb-6 border-t border-gray-200 pt-5">
          <h2 className="mb-2 text-lg font-semibold">Billing Summary</h2>
          <div>
            <strong>Annual Plan Price:</strong> {confirmation.financial.yearlyPriceDisplay}
          </div>
          <div>
            <strong>Payment Schedule:</strong> {confirmation.financial.paymentDisplay}
          </div>
          {confirmation.financial.billingAnchorDisplay ? (
            <div>
              <strong>Recurring Billing:</strong> {confirmation.financial.billingAnchorDisplay}
            </div>
          ) : null}
        </section>

        <section className="mb-6 border-t border-gray-200 pt-5">
          <h2 className="mb-2 text-lg font-semibold">Terms &amp; Conditions</h2>
          <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">
            {confirmation.plan.termsAndConditions?.trim().length
              ? confirmation.plan.termsAndConditions
              : 'No additional terms provided.'}
          </pre>
        </section>

        <footer className="border-t border-gray-200 pt-5 text-xs text-gray-500">
          <p>
            This page summarizes the current terms of your Maintenance Plan. For the most up-to-date information, please
            contact {confirmation.company.name}.
          </p>
          <p className="mt-1">
            {confirmation.company.name} — {phoneUtils.tryFormat(billingProfile.businessBillingContactPhoneNumber)} ·{' '}
            {billingProfile.businessBillingEmailAddress}
          </p>
        </footer>
      </ConfirmationPageShell>
    )
  },
)

export const MaintenancePlanConfirmationPage = React.memo(() => {
  const { companyGuid, maintenancePlanGuid } = useParams<{ companyGuid: string; maintenancePlanGuid: string }>()

  if (
    !companyGuid ||
    !maintenancePlanGuid ||
    !z.string().uuid().safeParse(companyGuid).success ||
    !z.string().uuid().safeParse(maintenancePlanGuid).success
  ) {
    return <ConfirmationNotFound />
  }

  return <ConfirmationPageLoader companyGuid={companyGuid} maintenancePlanGuid={maintenancePlanGuid} />
})

const ConfirmationPageLoader = React.memo(
  ({ companyGuid, maintenancePlanGuid }: { companyGuid: string; maintenancePlanGuid: string }) => {
    const { data, isLoading, isError } = trpc.maintenancePlans[
      unauthMaintenancePlanConfirmationDataProcedurePath
    ].useQuery(
      { companyGuid, maintenancePlanGuid },
      {
        cacheTime: 1000 * 60 * 5,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        retry: false,
      },
    )

    if (isLoading) {
      return FullScreenLoadingSpinner
    }
    if (isError || !data) {
      return <ConfirmationNotFound />
    }
    if (!data.downloadable) {
      return <ConfirmationStatusOnly data={data} />
    }
    return (
      <ConfirmationDownloadable
        confirmation={data.confirmation}
        companyGuid={data.companyGuid}
        maintenancePlanGuid={maintenancePlanGuid}
      />
    )
  },
)

MaintenancePlanConfirmationPage.displayName = 'MaintenancePlanConfirmationPage'
